/**
 * Types for the Campaigns module (quantix-api/docs/campaigns/TASKS.md, task W2).
 *
 * Mirrors the "Shared contract" section of that file exactly: table columns,
 * the field_mapping/source_fields JSON shapes (field-mapping.md), and the
 * CRM API endpoint list. The backend (T2/T3/T5) is being built in parallel —
 * where the contract doesn't spell out a response shape precisely, the type
 * below is a best-effort guess, called out in the W2 report so W3/W4 and the
 * API can reconcile it.
 */

import type { AvailableFields } from '@/components/leads/mapping/mappingValidation';

export type CampaignSource = 'facebook' | 'landing_page';

export type CampaignStatus = 'draft' | 'active' | 'paused' | 'archived';

export type CampaignSubmissionStatus =
    | 'pending'
    | 'lead_created'
    | 'linked_existing'
    | 'failed'
    | 'ignored';

/** One option of a Facebook multiple-choice question, or a landing-page expected value. */
export interface SourceFieldOption {
    key: string;
    label: string;
}

/**
 * One entry in `campaigns.source_fields`. For Facebook this is read from the
 * form's questions when it is linked; for landing pages it comes from a
 * captured test submission or manual entry (field-mapping.md §3).
 */
export interface SourceField {
    key: string;
    label: string;
    type: string;
    options?: SourceFieldOption[] | null;
    sample?: unknown;
}

/** Meta (Facebook) ad attribution keys stored on `campaign_submissions.meta`. */
export const META_AD_META_KEYS = [
    'ad_id',
    'ad_name',
    'adset_id',
    'adset_name',
    'campaign_id',
    'campaign_name',
    'form_id',
] as const;

export type MetaAdMetaKey = (typeof META_AD_META_KEYS)[number];

/** Mapping source key for a Meta ad field, e.g. `meta.ad_name`. */
export type MetaAdSourceKey = `meta.${MetaAdMetaKey}`;

/** Fixed "Meta ad" source field offered for Facebook campaigns (`campaign.meta_source_fields`). */
export interface MetaSourceField extends SourceField {
    key: MetaAdSourceKey;
    group: 'meta_ad';
}

/** One row of `campaigns.field_mapping.fields` (field-mapping.md §5). */
export interface FieldMappingEntry {
    /** Multiple source keys only for the combinable targets `name`/`notes` (§4.2). */
    source: string[];
    target: string;
    value_map?: Record<string, string> | null;
}

/** Shape of `campaigns.field_mapping` ("Mapping JSON" in the shared contract). */
export interface FieldMapping {
    fields: FieldMappingEntry[];
}

/** Counts by submission status, included on the list/detail endpoints. */
export interface CampaignSubmissionCounts {
    total: number;
    pending: number;
    lead_created: number;
    linked_existing: number;
    failed: number;
    ignored: number;
}

export interface CampaignLeadStatusRef {
    id: string;
    name: string;
    label: string;
    color?: string;
}

export interface CampaignAssigneeRef {
    id: string;
    name: string;
}

/** `facebook_page` on the campaign resource (contract: "`facebook_page` `{id,page_id,name}|null`"). */
export interface CampaignFacebookPageRef {
    id: string;
    page_id: string;
    name: string;
}

export interface Campaign {
    id: string;
    name: string;
    description: string | null;
    source: CampaignSource;
    status: CampaignStatus;
    default_lead_status_id: string | null;
    default_assignee_id: string | null;
    default_country: string | null;
    append_unmapped_to_notes: boolean;
    source_fields: SourceField[];
    /** Facebook campaigns only: fixed Meta ad fields, mappable like form questions. Empty otherwise. */
    meta_source_fields?: MetaSourceField[];
    field_mapping: FieldMapping | null;
    mapping_version: number;
    allowed_origins: string[] | null;
    facebook_page_id: string | null;
    facebook_form_id: string | null;
    facebook_form_name: string | null;
    facebook_form_status: string | null;
    facebook_last_polled_at: string | null;
    facebook_cursor_time: string | null;
    /**
     * Resolved Facebook page reference (contract: "`facebook_page` `{id,page_id,name}|null`").
     * Not in the original W2 type guess — added in W4 for the campaign detail page.
     */
    facebook_page?: CampaignFacebookPageRef | null;
    /**
     * Landing page only, only when the caller has CAMPAIGNS edit permission
     * (contract: "detail incl. endpoint_url (landing page, edit permission
     * only)"). Present on create and on GET /campaigns/{id}; absent otherwise.
     */
    endpoint_url?: string | null;
    submissions_count?: CampaignSubmissionCounts;
    default_lead_status?: CampaignLeadStatusRef | null;
    default_assignee?: CampaignAssigneeRef | null;
    created_by: string | null;
    updated_by: string | null;
    created_at: string;
    updated_at: string;
}

export interface CampaignListParams {
    page?: number;
    per_page?: number;
    search?: string;
    source?: CampaignSource;
    status?: CampaignStatus;
    sort_by?: string;
    sort_direction?: 'asc' | 'desc';
    [key: string]: any;
}

export interface CreateCampaignRequest {
    name: string;
    description?: string | null;
    source: CampaignSource;
    default_lead_status_id?: string | null;
    default_assignee_id?: string | null;
    default_country?: string | null;
    append_unmapped_to_notes?: boolean;
}

export interface UpdateCampaignRequest {
    name?: string;
    description?: string | null;
    default_lead_status_id?: string | null;
    default_assignee_id?: string | null;
    default_country?: string | null;
    append_unmapped_to_notes?: boolean;
    allowed_origins?: string[] | null;
    status?: CampaignStatus;
}

/** Landing page only — manual source field list (contract: PUT .../source-fields). */
export interface UpdateSourceFieldsRequest {
    source_fields: SourceField[];
}

/** Target fields for the mapping table, same shape as the import wizard's `available_fields`. */
export type CampaignFieldCatalog = AvailableFields;

export interface SaveCampaignMappingRequest {
    fields: FieldMappingEntry[];
    default_country?: string | null;
    append_unmapped_to_notes?: boolean;
}

export interface SaveCampaignMappingResponse {
    campaign: Campaign;
    errors: string[];
    warnings: string[];
}

export interface SuggestCampaignMappingResponse {
    fields: FieldMappingEntry[];
}

/** Applies the mapping to a sample payload or the latest submission — no save. */
export interface PreviewCampaignMappingRequest {
    sample?: Record<string, unknown>;
    submission_id?: string;
}

export interface PreviewCampaignMappingResponse {
    mapped_data: Record<string, unknown>;
    warnings: string[];
    errors: string[];
}

/** Landing page only. Rotating invalidates the previous token. */
export interface RotateCampaignTokenResponse {
    endpoint_url: string;
    token?: string;
}

export interface CampaignSubmission {
    id: string;
    campaign_id: string;
    external_id: string | null;
    status: CampaignSubmissionStatus;
    is_test: boolean;
    raw_payload?: Record<string, unknown>;
    /** Facebook: ad attribution (ad_id, ad_name, ...) plus platform etc.; landing page: utm_*, page_url, ... */
    meta?: (Partial<Record<MetaAdMetaKey, string | null>> & Record<string, unknown>) | null;
    mapping_version: number | null;
    mapped_data?: Record<string, unknown> | null;
    warnings?: string[] | null;
    errors?: string[] | null;
    lead_id: string | null;
    lead?: { id: string; name: string; type: 'LEAD' | 'CUSTOMER' } | null;
    received_at: string;
    processed_at: string | null;
    created_at: string;
    updated_at: string;
}

export interface CampaignSubmissionListParams {
    page?: number;
    per_page?: number;
    status?: CampaignSubmissionStatus;
    [key: string]: any;
}

export interface ReprocessFailedSubmissionsResponse {
    reprocessed: number;
}

export type FacebookConnectionStatus = 'active' | 'needs_reauth';

export interface FacebookConnection {
    id: string;
    fb_user_id: string;
    fb_user_name: string;
    status: FacebookConnectionStatus;
    last_error?: string | null;
    /** `null` when the token never expires (System User token generated with expiry "Never"). */
    token_expires_at?: string | null;
    /** `USER` or `SYSTEM_USER` as reported by Meta's debug_token. */
    token_type?: string | null;
    granted_scopes?: string[] | null;
    /** Recommended permissions (e.g. `ads_read`) the token doesn't have; a warning, not a blocker. */
    missing_recommended?: string[] | null;
}

export interface FacebookPage {
    id: string;
    page_id: string;
    name: string;
    facebook_connection_id: string;
}

export interface FacebookStatus {
    /** The tenant saved its Meta app credentials (Integrations > Facebook Lead Ads). */
    configured: boolean;
    /** A System User token is saved in Integrations > Facebook Lead Ads. */
    has_token: boolean;
    connected: boolean;
    connection: FacebookConnection | null;
    pages: FacebookPage[];
    required_permissions: string[];
    recommended_permissions: string[];
}

/** `POST /crm/campaigns/facebook/connect` 200 body `data` (also used as "Sync pages"). */
export interface FacebookConnectResult {
    connection: FacebookConnection;
    pages: FacebookPage[];
    warnings: string[];
}

/** `POST /crm/campaigns/facebook/connect` 422 body. */
export interface FacebookConnectErrorBody {
    errors: string[];
    missing_permissions?: string[];
}

export interface FacebookForm {
    id: string;
    name: string;
    status: string;
    /** Set when this form is already linked to a campaign (contract: "flag `linked_campaign_id`"). */
    linked_campaign_id: string | null;
}

export interface LinkFacebookFormRequest {
    facebook_page_id: string;
    facebook_form_id: string;
    import_backlog?: boolean;
}
